import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Snowflake, Flame, AlertTriangle, Loader2, Info, Mail, ShieldCheck, TrendingDown, BookOpen } from "lucide-react";

export interface MassEmailSettingsLite {
  warmVolumeWarnThreshold?: number;
  lockColdOverrideForNonAdmins?: boolean;
}

/**
 * Plain-language explainer popup covering safe warm-send volume, best
 * practices, when to use cold, risks, and what Clarik does to help.
 */
function WarmSendExplainerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="dialog-warm-explainer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            My Email vs. Cold Outreach — What You Need to Know
          </DialogTitle>
          <DialogDescription>
            A quick guide to picking the right send lane so your emails land where they should.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1 text-sm">

          <section className="space-y-1.5" data-testid="section-explainer-volume">
            <div className="flex items-center gap-2 font-semibold">
              <Mail className="w-4 h-4 text-primary shrink-0" />
              How many warm emails can I send per day?
            </div>
            <p className="text-muted-foreground leading-relaxed">
              For a regularly-used mailbox with a good reputation, <span className="text-foreground font-medium">roughly 10–30 emails per day</span> is a safe ballpark that's unlikely to raise any flags. This isn't a hard rule — a brand-new mailbox should stay closer to 10, while an older, well-established one can handle more. The key is consistency: a sudden spike of hundreds of emails from an inbox that normally sends a handful is what triggers spam filters.
            </p>
          </section>

          <section className="space-y-1.5" data-testid="section-explainer-warm">
            <div className="flex items-center gap-2 font-semibold">
              <Flame className="w-4 h-4 text-amber-600 shrink-0" />
              What is a "warm" email?
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Warm means the people on your list already know you — they've booked with you before, reached out to you, or opted in to hearing from you. Because your inbox's reputation is tied to your personal or business domain, sending from it signals authenticity. Warm sending works best for smaller lists of contacts who genuinely expect to hear from you. Think follow-ups, event confirmations, and personal check-ins.
            </p>
          </section>

          <section className="space-y-1.5" data-testid="section-explainer-cold">
            <div className="flex items-center gap-2 font-semibold">
              <Snowflake className="w-4 h-4 text-blue-500 shrink-0" />
              When should I use Cold instead?
            </div>
            <p className="text-muted-foreground leading-relaxed">
              If you're reaching out to new prospects who don't know you yet — or if you're sending to a larger list — use <span className="text-foreground font-medium">Cold mode</span>. Cold sending routes through a dedicated, verified domain that's separate from your personal inbox. That way, any deliverability bumps in the road don't affect your main email address. Cold mode is the right choice for prospect lists, lead outreach, and any bulk send over a few dozen recipients.
            </p>
          </section>

          <section className="space-y-1.5" data-testid="section-explainer-risk">
            <div className="flex items-center gap-2 font-semibold">
              <TrendingDown className="w-4 h-4 text-destructive shrink-0" />
              What happens if I send too much from my inbox?
            </div>
            <p className="text-muted-foreground leading-relaxed">
              If you exceed the safe daily range from your primary mailbox, email providers like Google and Microsoft start to see your address as a bulk sender. Over time, more of your emails land in the spam folder — even the one-on-one messages to clients you've worked with for years. In serious cases, your domain can get flagged and it takes weeks to recover. It's much easier to protect your reputation upfront than to repair it later.
            </p>
          </section>

          <section className="space-y-1.5" data-testid="section-explainer-clarik">
            <div className="flex items-center gap-2 font-semibold">
              <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
              What does Clarik do to protect you?
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Clarik automatically paces your sends so they go out during normal business hours, not all at once at 3 AM. A per-workspace daily cap limits how many emails can go out in a 24-hour window. If a send would exceed the cap, the extras are held and sent on the next business day — your contacts still hear from you, just without the deliverability risk of a mass blast. These guardrails apply in the background, so you don't have to think about timing or spacing.
            </p>
          </section>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-explainer-close">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Cold / Warm sending toggle shared by the mass-email composers and the drip
 * builder. Cold routes through the verified cold-sending domain (never the
 * user's own inbox); warm sends from the connected mailbox. Lead/prospect
 * lists default to cold; switching them to warm is an explicit, riskier choice.
 */
export function ColdWarmToggle({
  value,
  onChange,
  warmLocked,
  isLeadAudience,
  disabled,
}: {
  value: "cold" | "warm";
  onChange: (v: "cold" | "warm") => void;
  warmLocked: boolean;
  isLeadAudience: boolean;
  disabled?: boolean;
}) {
  const [explainerOpen, setExplainerOpen] = useState(false);

  return (
    <>
      <div className="space-y-2" data-testid="cold-warm-toggle">
        <Label className="text-sm text-muted-foreground">Send from</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange("cold")}
            className={`flex items-start gap-2 rounded-md border p-3 text-left transition-colors hover-elevate ${
              value === "cold" ? "border-primary bg-primary/5" : "border-border"
            }`}
            data-testid="button-mode-cold"
          >
            <Snowflake className={`w-4 h-4 mt-0.5 shrink-0 ${value === "cold" ? "text-primary" : "text-muted-foreground"}`} />
            <div>
              <p className="text-sm font-medium">Cold Outreach</p>
              <p className="text-xs text-muted-foreground">Routes via a dedicated relay — keeps your inbox safe.</p>
            </div>
          </button>
          <button
            type="button"
            disabled={disabled || warmLocked}
            onClick={() => onChange("warm")}
            className={`flex items-start gap-2 rounded-md border p-3 text-left transition-colors hover-elevate ${
              value === "warm" ? "border-amber-500 bg-amber-500/5" : "border-border"
            } ${warmLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="button-mode-warm"
          >
            <Flame className={`w-4 h-4 mt-0.5 shrink-0 ${value === "warm" ? "text-amber-600" : "text-muted-foreground"}`} />
            <div>
              <p className="text-sm font-medium">My Email</p>
              <p className="text-xs text-muted-foreground">Sends from your connected Gmail — best for existing clients.</p>
            </div>
          </button>
        </div>
        {warmLocked ? (
          <p className="text-xs text-muted-foreground" data-testid="text-warm-locked">
            An admin has locked cold/warm overrides — prospect/lead lists stay on Cold.
          </p>
        ) : value === "warm" && isLeadAudience ? (
          <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="text-warm-lead-warning">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <span>
              We don't recommend sending multiple emails from your primary mailbox to a prospect list.{" "}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
                onClick={() => setExplainerOpen(true)}
                data-testid="button-warm-explainer-link"
              >
                For more information, click here.
              </button>
            </span>
          </p>
        ) : null}
      </div>

      <WarmSendExplainerDialog open={explainerOpen} onOpenChange={setExplainerOpen} />
    </>
  );
}

/**
 * Deliberate acknowledge-gate for high-volume warm sends. Requires the
 * user to tick a checkbox before the (re)send is allowed — never a
 * single-click confirm.
 */
export function WarmVolumeAckDialog({
  open,
  onOpenChange,
  count,
  threshold,
  prospectOverride,
  onConfirm,
  isBusy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  threshold: number;
  prospectOverride: boolean;
  onConfirm: () => void;
  isBusy?: boolean;
}) {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="dialog-warm-volume-ack">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            High-volume warm send
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 pt-1 text-sm text-muted-foreground">
              <p data-testid="text-warm-ack-count">
                This warm campaign will send to <span className="font-semibold text-foreground">{count}</span> recipients
                (your warning threshold is {threshold}) directly from your connected mailbox.
              </p>
              {prospectOverride ? (
                <p className="text-amber-600">
                  These are prospect/lead contacts. Sending cold-style volume from your real inbox can seriously hurt
                  your primary domain's deliverability and land you in spam. Only proceed if these are genuinely warm,
                  opted-in contacts.
                </p>
              ) : (
                <p>Large warm blasts can affect your sender reputation. Make sure this list expects to hear from you.</p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer" data-testid="label-warm-ack-checkbox">
          <Checkbox
            checked={checked}
            onCheckedChange={(c) => setChecked(c === true)}
            className="mt-0.5"
            data-testid="checkbox-warm-ack"
          />
          <span className="text-sm">
            I understand this sends from my own inbox and I take responsibility for the deliverability impact.
          </span>
        </label>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-warm-ack-cancel">
            Cancel
          </Button>
          <Button
            disabled={!checked || isBusy}
            onClick={onConfirm}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="button-warm-ack-confirm"
          >
            {isBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Flame className="w-4 h-4 mr-2" />}
            Send anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
